/* eslint-disable react/prop-types */
import React, { useContext, useMemo, useRef, useEffect } from 'react';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import cx from 'utils/classnames';
import useOnScreen from 'hooks/custom/useOnScreen';
import GlobalContext from 'hooks/contexts/GlobalContext';
import { LogError } from 'utils/logging/LogError';
import { CLICK_PORTAL_CARD_LAYOUT } from 'consts';
import ResultCardHeader from './ResultCardHeader';
import ResultCardBody from './ResultCardBody';
import ResultCardFooter from './ResultCardFooter';
import PortalCard from './PortalCard';
import GridCard from './GridCard';
import useListingResultTracking from './useListingResultTracking';
import ResultCardContext from './ResultCardContext';

export default function ResultCard({
  schoolInfo,
  isLoading,
  index,
  badge,
  cardLayout,
}) {
  const ref = useRef();
  const hasBeenSeenRef = useRef(false);

  const {
    windowSize: { currentBreakpoint = 'mobile' },
    siteMeta: { clickPortalSettings } = {},
  } = useContext(GlobalContext);
  const ratingTooltipText = clickPortalSettings?.schoolRatingTooltipText;

  const { isOnScreen, wasOnScreenOnLoad, wasWithinViewPortOnLoad } =
    useOnScreen(ref, '-100px');

  const isMobile = currentBreakpoint === 'mobile';

  const isProgramView = !isEmpty(schoolInfo.selectedProgram);

  const listingResultTracking = useListingResultTracking(
    schoolInfo,
    isProgramView
  );

  function _debugClicksWithoutView(location) {
    if (!hasBeenSeenRef.current) {
      LogError('Click Triggered On Result Card Without View', {
        description: `location: ${location}`,
      });
    }
  }

  const handleAllProgramsClick = () => {
    _debugClicksWithoutView('allPrograms');
    listingResultTracking.clickedSeeAllPrograms();
  };

  const handleLogoClick = () => {
    _debugClicksWithoutView('logo');
    listingResultTracking.clickedLogo();
  };

  const handleTitleClick = () => {
    _debugClicksWithoutView('title');
    listingResultTracking.clickedTitle();
  };

  const handleLearnMoreClick = () => {
    _debugClicksWithoutView('learnMore');
    listingResultTracking.clickedLearnMore();
  };

  const handleProgramLinkClick = () => {
    _debugClicksWithoutView('programLink');
    listingResultTracking.clickedProgramLink();
  };

  const currentLink = isProgramView
    ? schoolInfo?.selectedProgram?.destinationUrl
    : schoolInfo?.url;

  useEffect(() => {
    const onscreenTimeout = setTimeout(() => {
      if (isOnScreen && !hasBeenSeenRef.current) {
        hasBeenSeenRef.current = true;
        listingResultTracking.viewedResult(
          wasOnScreenOnLoad,
          wasWithinViewPortOnLoad
        );
      }
    }, 500);
    return () => clearTimeout(onscreenTimeout);
  }, [isOnScreen]);

  const getSchoolLogoUrl = (imageMap = {}) => {
    return isMobile ? imageMap.mobile : imageMap.desktop;
  };

  const logoImg = useMemo(
    () => getSchoolLogoUrl(schoolInfo.schoolLogoUrl),
    [schoolInfo]
  );

  const resultCardContextValue = useMemo(
    () => ({
      ratingTooltipText,
      listingResultTracking,
      index,
      schoolName: schoolInfo?.name,
      isOffsiteConversion: schoolInfo?.isOffsiteConversion,
    }),
    [
      ratingTooltipText,
      listingResultTracking,
      index,
      schoolInfo?.name,
      schoolInfo?.isOffsiteConversion,
    ]
  );

  const resolvedCardLayout =
    cardLayout || clickPortalSettings?.resultCardLayout;
  const isPortalCard =
    resolvedCardLayout === CLICK_PORTAL_CARD_LAYOUT.PORTAL_CARD;
  const isGridCard = resolvedCardLayout === CLICK_PORTAL_CARD_LAYOUT.GRID_CARD;

  if (isGridCard) {
    return (
      <ResultCardContext.Provider value={resultCardContextValue}>
        <GridCard
          cardRef={ref}
          schoolInfo={schoolInfo}
          isLoading={isLoading}
          isMobile={isMobile}
          logoImg={logoImg}
          badge={badge}
          isProgramView={isProgramView}
          currentLink={currentLink}
          onLogoClick={handleLogoClick}
          onTitleClick={handleTitleClick}
          onCTAclick={handleLearnMoreClick}
          onProgramLinkClick={handleProgramLinkClick}
          onTotalCountClick={handleAllProgramsClick}
        />
      </ResultCardContext.Provider>
    );
  }

  if (isPortalCard) {
    return (
      <ResultCardContext.Provider value={resultCardContextValue}>
        <PortalCard
          cardRef={ref}
          schoolInfo={schoolInfo}
          isLoading={isLoading}
          logoImg={logoImg}
          badge={badge}
          isProgramView={isProgramView}
          currentLink={currentLink}
          onLogoClick={handleLogoClick}
          onTitleClick={handleTitleClick}
          onCTAclick={handleLearnMoreClick}
          onProgramLinkClick={handleProgramLinkClick}
          onTotalCountClick={handleAllProgramsClick}
        />
      </ResultCardContext.Provider>
    );
  }

  return (
    <ResultCardContext.Provider value={resultCardContextValue}>
      <article
        ref={ref}
        data-testid="resultCard"
        className={cx('resultCard', {
          'resultCard--isLoading': isLoading,
          resultCard__mobileContainer: isMobile,
        })}
      >
        <div className="resultCard__header">
          <ResultCardHeader
            isLoading={isLoading}
            isMobile={isMobile}
            logoImg={logoImg}
            name={schoolInfo.name}
            onLogoClick={handleLogoClick}
            onTitleClick={handleTitleClick}
            cardLink={currentLink}
            impressionGuid={schoolInfo.impressionGuid}
            isProgramView={isProgramView}
            badge={badge}
          />
        </div>
        <div className="resultCard__body">
          <ResultCardBody
            description={
              isProgramView
                ? get(schoolInfo, 'selectedProgram.shortDesc')
                : schoolInfo.description
            }
            name={schoolInfo.name}
            id={schoolInfo?.id?.toString()}
            isLoading={isLoading}
            isMobile={isMobile}
            isProgramView={isProgramView}
            learningType={schoolInfo.learningType}
            highlights={schoolInfo.highlights}
            logoImg={logoImg}
            onCTAclick={handleLearnMoreClick}
            onLogoClick={handleLogoClick}
            onTitleClick={handleTitleClick}
            cardLink={currentLink}
            rating={schoolInfo.rating}
            impressionGuid={schoolInfo.impressionGuid}
          />
        </div>

        <div className="resultCard__footer">
          <ResultCardFooter
            description={get(schoolInfo, 'selectedProgram.shortDesc')}
            review={schoolInfo.description}
            highlights={schoolInfo.highlights}
            isLoading={isLoading}
            isMobile={isMobile}
            isProgramView={isProgramView}
            onProgramLinkClick={handleProgramLinkClick}
            onTotalCountClick={handleAllProgramsClick}
            otherPrograms={schoolInfo.otherPrograms}
            selectedProgramName={get(schoolInfo, 'selectedProgram.label')}
            programCount={schoolInfo.programCount}
            cardLink={currentLink}
            schoolId={schoolInfo.id}
            filteredProgramCount={schoolInfo.filteredProgramCount}
            impressionGuid={schoolInfo.impressionGuid}
          />
        </div>
      </article>
    </ResultCardContext.Provider>
  );
}
